/**
 * File Upload Step
 * 
 * Drag-and-drop file upload with preview
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';

interface FileUploadStepProps {
  entityType: string;
  onFileUploaded: (session: any) => void;
  onError: (error: string) => void;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  entityType,
  onFileUploaded,
  onError
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      onError(`Invalid file type. Please upload ${validExtensions.join(', ')} files only.`);
      return;
    }

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      onError('File is too large. Maximum size is 50MB.');
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `/api/v1/import/upload?entity_type=${entityType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        onFileUploaded(response.data.session);
      } else {
        onError('File upload failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      onError(error.response?.data?.detail || 'File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadTemplate = () => {
    const url = `/api/v1/import/template/${entityType}`;
    window.open(url, '_blank');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Upload Your File
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Upload an Excel (.xlsx, .xls) or CSV file containing your {entityType} data.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          size="small"
        >
          Download Template
        </Button>
      </Box>

      {!file ? (
        <Paper
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.300',
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            }
          }}
        >
          <UploadIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag and drop your file here
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            or
          </Typography>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
          >
            Choose File
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
            />
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            Supported formats: Excel (.xlsx, .xls), CSV (.csv)
            <br />
            Maximum file size: 50MB
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <FileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`Size: ${formatFileSize(file.size)}`}
                />
                <Chip
                  icon={<SuccessIcon />}
                  label="Ready"
                  color="success"
                  size="small"
                />
              </ListItem>
            </List>
          </Paper>

          {uploading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Uploading and parsing file...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setFile(null)}
              disabled={uploading}
            >
              Choose Different File
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={<UploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload and Continue'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Help Section */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          💡 Tips for successful import:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Ensure your file has a header row with column names</li>
            <li>Remove any empty rows or columns</li>
            <li>Use consistent date formats (YYYY-MM-DD or DD/MM/YYYY)</li>
            <li>For US data: use proper ZIP codes, EIN numbers, and SSN numbers</li>
            <li>Check that email addresses are valid</li>
          </ul>
        </Typography>
      </Box>
    </Box>
  );
};

export default FileUploadStep;

